import * as React from "react";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import CircularProgress from "material-ui/CircularProgress";
import { $, merge } from "glamor";
import { Map, List } from "immutable";
import { formatMessage } from "lib/globalize-util";
import { theme } from "styles/theme";
import { Infinite, ICellProps } from "components/infinite";
import { ProductCollectionCatalogsTile } from "./product-collection-catalogs-tile";
import { debounce } from "lodash";

const PAGE_SIZE = 50;
const TILE_HEIGHT = 306;
const TILE_WIDTH = 265;

const cellDisplayInfo = {
    width: TILE_WIDTH,
    height: TILE_HEIGHT,
};

const groupDisplayInfo = {
    height: 80,
};

interface IProps {
    app: any;
    scope: any;
    location: any;
    router: any;
    onFetchCatalogs: (query) => void;
    onFetchCatalogsNextPage: (nextPage) => void;
    catalogsNextPage: any;
    entities?: any;
    ids?: string[];
    catalogIds: string[];
    catalogEntities: any;
    isFetchingCatalogs: boolean;
    brandId: string;
    catalogType?: "custom-list" | "linesheet";
    overrideStyles?: { headerHeight: number };
}

export class ProductCollectionCatalogsView extends React.PureComponent<IProps, any> {
    styles = merge(
        {
            height: "100%",
            width: "100%",
            padding: "12px 1px 0 0",
            display: "flex",
            flexDirection: "column",
        },
        $(" .loading", {
            display: "flex",
            justifyContent: "center",
            padding: "40px 0px",
        }),
        $(" .noBrand", {
            alignItems: "center",
            display: "flex",
            fontSize: "16px",
            fontWeight: "bold",
            height: "inherit",
            justifyContent: "center",
        }),
        $(" .results", {
            flexGrow: 1,
            flexShrink: 1,
            width: "100%",
            margin: "0",
            padding: "0",
            backgroundColor: "white",
        }),
        $(" .linesheetCell", {
            height: `${TILE_HEIGHT}px`,
            width: `${TILE_WIDTH}px`,
            cursor: "pointer",
        }),
        $(" .icon", {
            margin: "3px",
            fontSize: "30px",
            cursor: "pointer",
        }),
        $(" .image", {
            backgroundSize: "cover",
            backgroundColor: theme.palette.lightestGrey,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            margin: "0px",
            overflow: "hidden",
            position: "relative",
            height: `${TILE_WIDTH}px`,
        }),
        $(" .title", {
            color: theme.palette.darkerGrey,
            fontWeight: "bold",
            lineHeight: "20px",
            letterSpacing: "0.2px",
            fontSize: "14px",
            margin: "8px 0px 0px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            display: ["block", "-webkit-box"],
            height: "40px",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
        }),
        $(" .tombstoneTitle", {
            backgroundColor: theme.palette.lightestGrey,
            width: `${TILE_WIDTH}px`,
            height: "20px",
            margin: "8px 0px 0px",
        })
    );

    private infiniteRef: any;

    private muiStyles = {
        loading: {
            display: "block",
            margin: "60px auto 0px auto",
        },
    };

    constructor(props) {
        super(props);
        const showCatalogs =
            props.router.params.brand && props.scope.brand && props.scope.brand._id === props.router.params.brand;

        this.state = {
            filters: Map(),
            showCatalogs,
            switchingBrands: false,
        };
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowResize);
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleWindowResize);
        if (this.props.scope && this.props.scope.brand && this.props.scope.brand._id === this.props.brandId) {
            this.props.onFetchCatalogs({
                active: true,
                pageSize: PAGE_SIZE,
                type: this.props.catalogType,
            });
        }
    }

    componentDidUpdate(prevProps: IProps) {
        if (
            !this.props.isFetchingCatalogs &&
            (this.props.catalogIds !== prevProps.catalogIds || this.props.catalogEntities !== prevProps.catalogEntities)
        ) {
            const catalogs = this.props.catalogIds
                .map((id) => {
                    return this.props.catalogEntities[id];
                })
                .filter((catalog) => {
                    return catalog.brand_id === this.props.brandId;
                });

            this.setState({ catalogs: List(catalogs) });
        }

        if (
            (prevProps.scope.brand &&
                this.props.scope.brand &&
                prevProps.scope.brand._id !== this.props.scope.brand._id) ||
            (!prevProps.scope.brand && this.props.scope.brand) ||
            prevProps.catalogType !== this.props.catalogType
        ) {
            this.props.onFetchCatalogs({
                active: true,
                pageSize: PAGE_SIZE,
                type: this.props.catalogType,
            });
            this.setState({ switchingBrands: false });
        }

        if (this.props.brandId !== prevProps.brandId) {
            this.setState({ switchingBrands: true });
        }

        if (
            prevProps.scope.brand &&
            this.props.scope.brand &&
            prevProps.scope.brand._id === this.props.scope.brand._id
        ) {
            if (this.state.switchingBrands === true) {
                this.setState({ switchingBrands: false });
            }
        }
    }

    // tslint:disable-next-line
    handleWindowResize = debounce(() => this.forceUpdate(), 200);

    handleLoadMoreCatalogs = () => {
        const { catalogsNextPage, onFetchCatalogsNextPage, isFetchingCatalogs } = this.props;
        if (catalogsNextPage && !isFetchingCatalogs) {
            onFetchCatalogsNextPage(catalogsNextPage);
        }
    };

    handleSelectLinesheet = (id) => {
        const { router } = this.props;
        const { pathname, query } = router.location;
        router.push({
            pathname: `${pathname}/catalog/${id}`,
            query,
        });
    };

    render() {
        const { scope, isFetchingCatalogs, catalogsNextPage, overrideStyles = { headerHeight: 0 } } = this.props;
        const { catalogs, switchingBrands } = this.state;
        const { loading } = this.muiStyles;
        const { headerHeight } = overrideStyles;

        let content: React.ReactNode;
        if (!scope.brand) {
            content = <div className="noBrand">{formatMessage("Choose a brand to browse catalog")}</div>;
        } else if (switchingBrands) {
            content = <CircularProgress style={loading} />;
        } else if (catalogs) {
            content = (
                <div className="results">
                    <AutoSizer>
                        {({ width, height }) => {
                            const gutterWidth = 12;
                            return (
                                <Infinite
                                    containerHeight={height - headerHeight}
                                    containerWidth={width}
                                    cellRenderer={this.handleCellRender}
                                    items={catalogs}
                                    onGetCellDisplayInfo={cellDisplayInfo}
                                    sortable={false}
                                    itemIdPath={["id"]}
                                    onGetGroupDisplayInfo={groupDisplayInfo}
                                    onClick={this.handleSelectLinesheet}
                                    isFetching={isFetchingCatalogs}
                                    onLoadMore={this.handleLoadMoreCatalogs}
                                    hasMoreResults={!!catalogsNextPage}
                                    ref={this.bindInfinite}
                                    // Show all linesheets
                                    infinite={false}
                                    centered={true}
                                    // maxWidth={880 + gutterWidth * 2}
                                    gutterWidth={gutterWidth}
                                />
                            );
                        }}
                    </AutoSizer>
                </div>
            );
        }

        return <div {...this.styles}>{content}</div>;
    }

    private handleCellRender = (props: ICellProps): React.ReactNode => {
        return <ProductCollectionCatalogsTile {...props} brandId={this.props.brandId} />;
    };

    private bindInfinite = (ref) => (this.infiniteRef = ref);
}
